import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import Toster from "../common/Toster";
import Pagination from "../common/Pagination";
import {
  AdminPageLayout,
  AdminPageHeader,
  AdminPageCard,
  AdminPageCardToolbar,
} from "../common/AdminPageLayout";
import { SUPPLIER_PAGE_META } from "../common/supplierPageMeta";
import { getTableEmptyProps } from "../common/supplierTableEmpty";
import {
  SupplierFilterToolbar,
  SupplierFilterField,
} from "../common/supplierPageUi";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  AdminTableFooter,
  TableSearchInput,
  TableEmptyRow,
  ViewDetailsButton,
  TableActionsCell,
  StatusBadge,
} from "../common/adminTable";
import { list, search, markAsRead, getById } from "../../services/enquiryService";
import Dialog from "../dialog/Dialog";
import { formatListingDate } from "../../utils/formatListingDate";

const Enquiry = () => {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedEnquiryId, setSelectedEnquiryId] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  const { data: enquiriesData } = useQuery({
    queryKey: ["enquiries", currentPage, searchQuery],
    queryFn: () =>
      searchQuery
        ? search({ search: searchQuery, page: currentPage })
        : list({ page: currentPage }),
    keepPreviousData: true,
  });

  const { data: selectedEnquiryData, isLoading: isLoadingDetails } = useQuery({
    queryKey: ["enquiry", selectedEnquiryId],
    queryFn: () => getById(selectedEnquiryId),
    enabled: !!selectedEnquiryId,
  });

  const markAsReadMutation = useMutation({
    mutationFn: markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["enquiries"] });
      queryClient.invalidateQueries({ queryKey: ["enquiry", selectedEnquiryId] });
      showSuccessMessage("Enquiry marked as read");
    },
    onError: (error) => {
      showErrorMessage(error.message || "Failed to mark enquiry as read");
    },
  });

  const enquiries = enquiriesData?.data?.data || [];
  const selectedEnquiry = selectedEnquiryData?.data;
  const pagination = {
    currentPage: enquiriesData?.data?.current_page || 1,
    lastPage: enquiriesData?.data?.last_page || 1,
    perPage: enquiriesData?.data?.per_page || 10,
    total: enquiriesData?.data?.total || 0,
    from: enquiriesData?.data?.from || 0,
    to: enquiriesData?.data?.to || 0,
  };

  const showSuccessMessage = (message) => {
    setSuccessMessage(message);
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      setSuccessMessage("");
    }, 3000);
  };

  const showErrorMessage = () => {
    setShowError(true);
    setTimeout(() => {
      setShowError(false);
    }, 3000);
  };

  const handlePageChange = (page) => {
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const handleViewDetails = (enquiry) => {
    setSelectedEnquiryId(enquiry.id);
    setShowDetailsDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDetailsDialog(false);
    setSelectedEnquiryId(null);
  };

  const handleMarkAsRead = () => {
    if (selectedEnquiryId) {
      markAsReadMutation.mutate(selectedEnquiryId, {
        onSuccess: () => {
          handleCloseDialog();
        },
      });
    }
  };

  const formatDate = formatListingDate;
  const pageMeta = SUPPLIER_PAGE_META.enquiry;

  return (
    <AdminPageLayout>
      {showError && (
        <Toster
          message="An error occurred"
          type="error"
          duration={3000}
          onClose={() => setShowError(false)}
        />
      )}

      {showSuccess && successMessage && (
        <Toster
          message={successMessage}
          type="success"
          duration={3000}
          onClose={() => {
            setShowSuccess(false);
            setSuccessMessage("");
          }}
        />
      )}

      <AdminPageHeader title={pageMeta.title} subtitle={pageMeta.subtitle} />

      <AdminPageCard>
        <AdminPageCardToolbar>
          <SupplierFilterToolbar
            showReset={!!searchQuery}
            onReset={() => setSearchQuery("")}
          >
            <SupplierFilterField label="Search">
              <TableSearchInput
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, email, phone..."
                className="mt-1 w-full sm:w-full"
              />
            </SupplierFilterField>
          </SupplierFilterToolbar>
        </AdminPageCardToolbar>

        <AdminTable
          className="shadow-none rounded-none"
          footer={
            <AdminTableFooter>
              <Pagination
                pagination={pagination}
                onPageChange={handlePageChange}
                showInfo={true}
              />
            </AdminTableFooter>
          }
        >
          <AdminTableHead>
            <AdminTableTh>#</AdminTableTh>
            <AdminTableTh>Name</AdminTableTh>
            <AdminTableTh>Email</AdminTableTh>
            <AdminTableTh>Phone</AdminTableTh>
            <AdminTableTh>Received Date</AdminTableTh>
            <AdminTableTh>Status</AdminTableTh>
            <AdminTableTh align="right">Actions</AdminTableTh>
          </AdminTableHead>
          <AdminTableBody>
            {enquiries.length > 0 ? (
              enquiries.map((enquiry, index) => (
                <AdminTableRow
                  key={enquiry.id}
                  className={!enquiry.is_read ? "bg-blue-50/50" : ""}
                >
                  <AdminTableCell>{index + 1}</AdminTableCell>
                  <AdminTableCell>{enquiry.name}</AdminTableCell>
                  <AdminTableCell>{enquiry.email}</AdminTableCell>
                  <AdminTableCell>{enquiry.phone}</AdminTableCell>
                  <AdminTableCell>{formatDate(enquiry.created_at)}</AdminTableCell>
                  <AdminTableCell>
                    <StatusBadge
                      variant={enquiry.is_read ? "read" : "unread"}
                      withRing
                    >
                      {enquiry.is_read ? "Read" : "Unread"}
                    </StatusBadge>
                  </AdminTableCell>
                  <TableActionsCell>
                    <ViewDetailsButton onClick={() => handleViewDetails(enquiry)} />
                  </TableActionsCell>
                </AdminTableRow>
              ))
            ) : (
              <TableEmptyRow
                colSpan={7}
                {...getTableEmptyProps("enquiries", {
                  hasActiveFilters: !!searchQuery,
                  onClearFilters: () => setSearchQuery(""),
                })}
              />
            )}
          </AdminTableBody>
        </AdminTable>
      </AdminPageCard>

      {showDetailsDialog && selectedEnquiry && (
        <Dialog
          title="Enquiry Details"
          open={showDetailsDialog}
          onClose={handleCloseDialog}
          width="w-3/4"
          primaryButtonText="Mark as Read"
          primaryButtonAction={handleMarkAsRead}
          primaryButtonClassName={`${selectedEnquiry.is_read ? "bg-green-600" : "bg-primary"}`}
          showCloseButton={true}
        >
          {isLoadingDetails ? (
            <div className="flex justify-center items-center p-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                <StatusBadge
                  variant={selectedEnquiry.is_read ? "read" : "unread"}
                  withRing
                >
                  {selectedEnquiry.is_read ? "Read" : "Unread"}
                </StatusBadge>
                {selectedEnquiry.read_at && (
                  <span className="text-sm text-gray-500">
                    Read at: {selectedEnquiry.read_at}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-4 rounded-lg border">
                  <h3 className="text-lg font-semibold mb-4 text-gray-800">Enquiry Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Travel Date</p>
                      <p className="mt-1">{formatDate(selectedEnquiry.enquiry_date)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Number of Adults</p>
                      <p className="mt-1">{selectedEnquiry.adult}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Number of Children</p>
                      <p className="mt-1">{selectedEnquiry.child}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Number of Infants</p>
                      <p className="mt-1">{selectedEnquiry.infant}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Created</p>
                      <p className="mt-1">{formatDate(selectedEnquiry.created_at)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Stay Category</p>
                      <p className="mt-1 capitalize">{selectedEnquiry.stay_category?.title || "N/A"}</p>
                    </div>
                  </div>
                </div>

                {selectedEnquiry.package && (
                  <div className="bg-white p-4 rounded-lg border">
                    <h3 className="text-lg font-semibold mb-4 text-gray-800">Package Details</h3>
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Package Name</p>
                        <p className="mt-1 text-lg font-medium text-gray-900">{selectedEnquiry.package.name}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Tour Type</p>
                        <p className="mt-1 capitalize">{selectedEnquiry.package.tour_type.replace(/_/g, " ")}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Duration</p>
                        <p className="mt-1">{selectedEnquiry.package.total_days} Days / {selectedEnquiry.package.total_nights} Nights</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Group Size</p>
                        <p className="mt-1">Min: {selectedEnquiry.package.minimum_members} / Max: {selectedEnquiry.package.maximum_members}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Starting Location</p>
                        <p className="mt-1">{selectedEnquiry.package.starting_location}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium text-gray-500">Pickup Point</p>
                          <p className="mt-1">{selectedEnquiry.package.pickup_point}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Drop-off Point</p>
                          <p className="mt-1">{selectedEnquiry.package.dropoff_point}</p>
                        </div>
                      </div>

                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <h4 className="text-md font-semibold mb-3 text-gray-800">Supplier Details</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm font-medium text-gray-500">Company Name</p>
                            <p className="mt-1">{selectedEnquiry.package.user?.supplier_details?.company_name || "N/A"}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-500">Contact Person</p>
                            <p className="mt-1">{selectedEnquiry.package.user?.name || "N/A"}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-500">Email</p>
                            <p className="mt-1">{selectedEnquiry.package.user?.email || "N/A"}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-500">Phone</p>
                            <p className="mt-1">{selectedEnquiry.package.user?.phone || "N/A"}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </Dialog>
      )}
    </AdminPageLayout>
  );
};

export default Enquiry;
